import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowMediaPlaylistsDto, MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import { resolveFlowSlotIdFromCueRow } from '@/helpers/simulation-cue-playability.helper';
import { isFlowControlInBetweenSegmentKind, type FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { isUntimedSlot } from '@/components/flows/detail/slot-list/slot-timing';

export type CueSheetRundownInBetween = {
  segmentKind: Extract<FlowControlSegmentKind, 'flow_transition' | 'flow_resume'>;
  slotId: string;
  rows: SimulationCueSheetRow[];
};

export type CueSheetRundownSection = {
  key: string;
  segmentKind: string;
  slotId?: string;
  rows: SimulationCueSheetRow[];
  /** Transition/resume playlist folded under the preceding session/break (same slotId). */
  trailingInBetween?: CueSheetRundownInBetween;
  /**
   * Playlist still exists but every virtual slug is disabled and every media ref is skipped.
   * Control keeps the section so the operator can turn items back on.
   */
  playlistSkipped?: boolean;
};

export function rundownSectionKey(segmentKind: string, slotId?: string): string {
  return `${segmentKind}::${slotId ?? ''}`;
}

/** Group sheet rows in first-appearance order. Key is segmentKind + slotId (split-sessions stay two sections). */
export function groupSheetRows(rows: SimulationCueSheetRow[]): CueSheetRundownSection[] {
  const sections: CueSheetRundownSection[] = [];
  const indexByKey = new Map<string, number>();
  for (const row of rows) {
    const slotId = resolveFlowSlotIdFromCueRow(row);
    const key = rundownSectionKey(row.segmentKind, slotId);
    const existing = indexByKey.get(key);
    if (existing === undefined) {
      indexByKey.set(key, sections.length);
      sections.push({ key, segmentKind: row.segmentKind, slotId, rows: [row] });
    } else {
      sections[existing]!.rows.push(row);
    }
  }
  return sections;
}

/**
 * Nest flow_transition / flow_resume under the session or break they follow
 * (same slotId) so Control does not paint them as extra rundown rows.
 */
export function foldInBetweenIntoPrecedingSections(sections: CueSheetRundownSection[]): CueSheetRundownSection[] {
  const folded: CueSheetRundownSection[] = [];
  for (const section of sections) {
    if (isFlowControlInBetweenSegmentKind(section.segmentKind) && section.slotId) {
      const parent = [...folded]
        .reverse()
        .find(
          (entry) =>
            entry.slotId === section.slotId && (entry.segmentKind === 'session' || entry.segmentKind === 'break'),
        );
      if (parent) {
        parent.trailingInBetween = {
          segmentKind: section.segmentKind,
          slotId: section.slotId,
          rows: section.rows,
        };
        continue;
      }
    }
    folded.push({ ...section });
  }
  return folded;
}

/** Append effective-flow slots that have no sheet section yet (empty chrome at the end). */
export function appendEmptyEffectiveFlowSlots(
  sections: CueSheetRundownSection[],
  slots: FlowSlotDto[] | undefined,
): CueSheetRundownSection[] {
  if (!slots?.length) {
    return sections;
  }
  const usedSlotIds = new Set(sections.map((section) => section.slotId).filter((id): id is string => Boolean(id)));
  const extras = slots.filter((slot) => slot.id && !usedSlotIds.has(slot.id) && !isUntimedSlot(slot));
  if (!extras.length) {
    return sections;
  }
  return [
    ...sections,
    ...extras.map((slot) => {
      const segmentKind = slot.contentType === 'Break' ? 'break' : 'session';
      return {
        key: rundownSectionKey(segmentKind, slot.id),
        segmentKind,
        slotId: slot.id,
        rows: [],
      };
    }),
  ];
}

/** Control is not set up for Session Pool / untimed slots — drop those rundown rows. */
export function omitUntimedSlotsFromRundown(
  sections: CueSheetRundownSection[],
  slots: FlowSlotDto[] | undefined,
): CueSheetRundownSection[] {
  if (!slots?.length) {
    return sections;
  }
  const untimedIds = new Set(slots.filter((slot) => slot.id && isUntimedSlot(slot)).map((slot) => slot.id));
  if (untimedIds.size === 0) {
    return sections;
  }
  return sections.filter((section) => !section.slotId || !untimedIds.has(section.slotId));
}

type FlowPlaylistRundownSource = {
  mediaPlaylists?: FlowMediaPlaylistsDto;
  slots?: FlowSlotDto[];
};

/** True when every cue row is persisted-skip or not playable. Empty sections are not skipped. */
export function isCueRowsFullySkipped(
  rows: Array<Pick<SimulationCueSheetRow, 'operatorSkipped' | 'isPlayable'>>,
): boolean {
  return rows.length > 0 && rows.every((row) => row.operatorSkipped === true || row.isPlayable === false);
}

/**
 * Operator turned every virtual slug off and marked every media ref skip.
 * An empty playlist is not skipped — it still uses the info fallback.
 */
export function isFlowPlaylistFullySkipped(playlist: MediaPlaylistDto | undefined): boolean {
  if (!playlist?.id) {
    return false;
  }
  const applicable = playlist.applicableVirtualMedia ?? [];
  const enabled = new Set(playlist.effectiveVirtualMedia?.enabledSlugs ?? []);
  if (applicable.some((item) => enabled.has(item.slug))) {
    return false;
  }
  const refs = playlist.refs ?? [];
  if (refs.some((ref) => ref.advanceMode !== 'skip')) {
    return false;
  }
  return applicable.length > 0 || refs.length > 0;
}

function timedRundownSlots(slots: FlowSlotDto[] | undefined): FlowSlotDto[] {
  return (slots ?? []).filter((slot) => slot.id && slot.contentType !== 'Gap' && !isUntimedSlot(slot));
}

function skippedPlaylistSection(segmentKind: string, slotId?: string): CueSheetRundownSection {
  return {
    key: rundownSectionKey(segmentKind, slotId),
    segmentKind,
    slotId,
    rows: [],
    playlistSkipped: true,
  };
}

/**
 * Cue sheets omit a scope once every virtual item is disabled and no playable media remains.
 * Put that scope back in Control order, still marked skipped, so the operator can turn items on.
 */
export function retainFullySkippedFlowPlaylistSections(
  sections: CueSheetRundownSection[],
  flow: FlowPlaylistRundownSource | null | undefined,
): CueSheetRundownSection[] {
  const playlists = flow?.mediaPlaylists;
  if (!playlists) {
    return sections;
  }

  const result = sections.map((section) => ({ ...section, rows: [...section.rows] }));

  const indexOf = (segmentKind: string, slotId?: string): number =>
    result.findIndex((section) => section.key === rundownSectionKey(segmentKind, slotId));

  const ensure = (
    segmentKind: string,
    slotId: string | undefined,
    playlist: MediaPlaylistDto | undefined,
    at: number,
  ) => {
    if (!isFlowPlaylistFullySkipped(playlist)) {
      return;
    }
    const existing = indexOf(segmentKind, slotId);
    if (existing >= 0) {
      if (isCueRowsFullySkipped(result[existing]!.rows)) {
        result[existing] = { ...result[existing]!, playlistSkipped: true };
      }
      return;
    }
    const index = Math.min(Math.max(at, 0), result.length);
    result.splice(index, 0, skippedPlaylistSection(segmentKind, slotId));
  };

  ensure('pre_flow', undefined, playlists.pre_flow, 0);
  const preIndex = indexOf('pre_flow');
  ensure('flow_intro', undefined, playlists.flow_intro, preIndex >= 0 ? preIndex + 1 : 0);

  const slots = timedRundownSlots(flow?.slots);
  let cursor = indexOf('flow_intro');
  if (cursor < 0) {
    cursor = indexOf('pre_flow');
  }
  cursor = cursor >= 0 ? cursor + 1 : 0;

  slots.forEach((slot, slotIndex) => {
    const slotId = slot.id!;
    const slotKind = slot.contentType === 'Break' ? 'break' : 'session';
    if (slot.contentType === 'Break') {
      ensure('break', slotId, playlists.slots?.[slotId], cursor);
    }
    const slotSection = indexOf(slotKind, slotId);
    if (slotSection >= 0) {
      cursor = slotSection + 1;
    }
    if (slotIndex >= slots.length - 1) {
      return;
    }
    if (slot.contentType === 'Break') {
      ensure('flow_resume', slotId, playlists.flow_resume, cursor);
      const resumeIndex = indexOf('flow_resume', slotId);
      if (resumeIndex >= 0) {
        cursor = resumeIndex + 1;
      }
    } else if (slot.contentType === 'Session') {
      ensure('flow_transition', slotId, playlists.flow_transition, cursor);
      const transitionIndex = indexOf('flow_transition', slotId);
      if (transitionIndex >= 0) {
        cursor = transitionIndex + 1;
      }
    }
  });

  const afterIndex = indexOf('after_flow');
  ensure('flow_outro', undefined, playlists.flow_outro, afterIndex >= 0 ? afterIndex : result.length);
  const outroIndex = indexOf('flow_outro');
  ensure('after_flow', undefined, playlists.after_flow, outroIndex >= 0 ? outroIndex + 1 : result.length);

  return result;
}
